import { useEffect, useState, useRef } from 'react';

interface UseFormTextAreaOptions {
    name: string;
    initialValue?: string;
    onFieldInteracted?: (name: string, element: HTMLElement | null) => void;
    validationFunc?: (value: string) => Promise<[boolean, string]>;
}

interface UseFormTextAreaReturn {
    ref: React.RefObject<HTMLTextAreaElement>;
    value: string;
    setValue: (value: string) => void;
    dirty: boolean;
    valid: boolean;
    reason: string;
    pending: boolean;
    touched: boolean;
    onFocus: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
    isEmpty: () => boolean;
}

export const useFormTextArea = (options: UseFormTextAreaOptions): UseFormTextAreaReturn => {
    const { name, initialValue = '', onFieldInteracted, validationFunc } = options;

    const [value, setValue] = useState(initialValue);
    const [dirty, setDirty] = useState(false);
    const [valid, setValid] = useState(false);
    const [pending, setPending] = useState(false);
    const [touched, setTouched] = useState(false);
    const [reason, setReason] = useState('');
    const ref = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (touched && dirty && onFieldInteracted) {
            onFieldInteracted(name, ref.current);
        }
    }, [name, dirty, touched, onFieldInteracted]);

    useEffect(() => {
        if (validationFunc) {
            validate(initialValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;

        setValue(newValue);
        setValid(false);
        setReason('');

        if (dirty) {
            setDirty(false);
        }

        if (!touched) {
            setTouched(true);
        }
    };

    const onBlur = async (event: React.FocusEvent<HTMLTextAreaElement>) => {
        const { value } = event.target;
        const formattedValue = value.trim();

        setValue(formattedValue);
        setDirty(true);

        if (validationFunc) {
            await validate(formattedValue);
        }
    };

    const validate = async (value: string) => {
        if (!validationFunc) {
            setValid(true);
            setReason('');
            setPending(false);
            return;
        }

        setPending(true);

        try {
            const [status, reason] = await validationFunc(value);
            setValid(status);
            setReason(reason);
        } catch (error) {
            setValid(false);
            setReason('Validation error');
        } finally {
            setPending(false);
        }
    };

    const isEmpty = () => value === '';

    return {
        ref,
        value,
        setValue,
        dirty,
        valid,
        reason,
        pending,
        touched,
        // eslint-disable-next-line @typescript-eslint/no-empty-function, no-empty-function
        onFocus: () => {},
        onChange,
        onBlur,
        isEmpty,
    };
};
